import React from 'react';
import {
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControl,
  styled,
  SelectProps,
} from '@mui/material';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectV2Props extends Omit<SelectProps, 'value' | 'onChange'> {
  options: SelectOption[];
  value?: string | number;
  onChange?: (event: SelectChangeEvent<unknown>) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  dataQa?: string;
}

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  minWidth: 180,
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.common.whiteBorder}`,

    '&:hover': {
      borderColor: theme.palette.common.cowbellLight,
    },
  },

  '& .MuiSelect-select': {
    padding: '8px 12px',
    fontSize: '0.875rem',
    fontFamily: theme.config.fonts.stackSans,
    color: theme.palette.text.primary,

    '&.Mui-disabled': {
      color: theme.palette.text.disabled,
      backgroundColor: theme.palette.background.disabled,
    },
  },

  '& .MuiSelect-icon': {
    color: theme.palette.text.secondary,

    '&.Mui-disabled': {
      color: theme.palette.text.disabled,
    },
  },

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiMenu-paper': {
    marginTop: '4px',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.common.whiteBorder}`,
    boxShadow:
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',

    '& .MuiList-root': {
      padding: '4px 0',
    },
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: '0.875rem',
  fontFamily: theme.config.fonts.stackSans,
  color: theme.palette.text.primary,
  padding: '8px 16px',

  '&:hover': {
    backgroundColor: theme.palette.background.active,
  },

  '&.Mui-selected': {
    backgroundColor: `${theme.palette.common.cowbellBlue} !important`,
    color: theme.palette.common.white,

    '&:hover': {
      backgroundColor: theme.palette.common.cowbellBlue,
      color: theme.palette.common.white,
    },
  },

  '&.Mui-disabled': {
    color: theme.palette.text.disabled,
    opacity: 0.6,
  },
}));

export const PlaceholderText = styled('span')(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontStyle: 'italic',
}));

export const SelectV2: React.FC<SelectV2Props> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  className,
  dataQa,
  ...props
}) => {
  const renderValue = (selected: unknown): React.ReactNode => {
    if (!selected || selected === '') {
      return <PlaceholderText>{placeholder}</PlaceholderText>;
    }

    const option = options.find((opt) => opt.value === selected);
    return option?.label || String(selected);
  };

  return (
    <StyledFormControl className={className}>
      <StyledSelect
        value={value || ''}
        onChange={onChange}
        disabled={disabled}
        displayEmpty
        renderValue={renderValue}
        variant="outlined"
        inputProps={{
          'data-qa': dataQa,
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
        {...props}
      >
        {options.map((option) => (
          <StyledMenuItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </StyledMenuItem>
        ))}
      </StyledSelect>
    </StyledFormControl>
  );
};
